import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Package } from "lucide-react";
import { toast } from "sonner";
import { Money } from "@/components/wallet/shared";
import { useWalletItems } from "@/components/wallet/ItemsDialog";

export function useWalletPackages() {
  return useQuery({ queryKey: ["wallet-packages"], queryFn: async () => (await api.get("/wallet/packages")).data });
}

export function PackageLabel({ p }) {
  const saving = p.full_price - p.price;
  return (
    <span>
      <b>{p.name}</b> · {p.quantity} × {p.item_name} a <Money value={p.price}/>
      {saving > 0 && <span className="text-emerald-700"> (risparmio <Money value={saving}/>)</span>}
    </span>
  );
}

const EMPTY = { name: "", item_id: "", quantity: "", price: "" };

export default function PackagesDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: items = [] } = useWalletItems();
  const { data: pkgs = [] } = useWalletPackages();
  const [f, setF] = useState(EMPTY);
  const inval = () => qc.invalidateQueries({ queryKey: ["wallet-packages"] });
  const onErr = (e) => toast.error(e.response?.data?.detail || "Errore");
  const item = items.find((i) => i.id === f.item_id);
  const add = useMutation({
    mutationFn: async () => (await api.post("/wallet/packages", { ...f, quantity: parseInt(f.quantity, 10), price: parseFloat(f.price) })).data,
    onSuccess: () => { toast.success("Pacchetto creato"); setF(EMPTY); inval(); }, onError: onErr,
  });
  const upd = useMutation({ mutationFn: async (p) => (await api.patch(`/wallet/packages/${p.id}`, p)).data, onSuccess: inval, onError: onErr });
  const del = useMutation({ mutationFn: async (id) => (await api.delete(`/wallet/packages/${id}`)).data, onSuccess: inval, onError: onErr });
  const valid = f.name.trim() && f.item_id && parseInt(f.quantity, 10) > 0 && parseFloat(f.price) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="packages-dialog">
        <DialogHeader>
          <DialogTitle>Pacchetti prepagati</DialogTitle>
          <DialogDescription>Es. 12 caffè a 10 € invece di 12 €. Le unità acquistate si usano prima del prezzo normale.</DialogDescription>
        </DialogHeader>
        {items.length === 0 ? <div className="text-sm text-stone-500">Crea prima le voci nel Listino.</div> : (
          <div className="p-3 rounded-lg bg-stone-50 border border-stone-200 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nome (es. Carnet caffè)" data-testid="pkg-name"/>
              <Select value={f.item_id} onValueChange={(v) => setF({ ...f, item_id: v })}>
                <SelectTrigger data-testid="pkg-item"><SelectValue placeholder="Voce di listino"/></SelectTrigger>
                <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} · € {i.price.toFixed(2)}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" min="1" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} placeholder="Quantità" data-testid="pkg-qty"/>
              <Input type="number" step="0.01" min="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="Prezzo pacchetto €" data-testid="pkg-price"/>
            </div>
            {item && f.quantity && <div className="text-[11px] text-stone-500">Prezzo pieno: € {(item.price * (parseInt(f.quantity, 10) || 0)).toFixed(2)}</div>}
            <Button className="w-full bg-[#0F766E] hover:bg-[#115E59]" disabled={!valid || add.isPending} onClick={() => add.mutate()} data-testid="pkg-add"><Plus size={14} className="mr-1"/> Crea pacchetto</Button>
          </div>
        )}
        <ul className="divide-y divide-stone-100">
          {pkgs.length === 0 && <li className="py-6 text-center text-sm text-stone-400">Nessun pacchetto.</li>}
          {pkgs.map((p) => (
            <li key={p.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`pkg-${p.id}`}>
              <Package size={14} className="text-[#0F766E] shrink-0"/>
              <span className={`flex-1 ${p.active ? "" : "opacity-50"}`}><PackageLabel p={p}/></span>
              <Switch checked={p.active} onCheckedChange={(v) => upd.mutate({ ...p, active: v })} data-testid={`pkg-active-${p.id}`}/>
              <button type="button" onClick={() => { if (window.confirm(`Eliminare "${p.name}"? Le unità già acquistate restano valide.`)) del.mutate(p.id); }} className="text-stone-400 hover:text-red-600" data-testid={`pkg-del-${p.id}`}><Trash2 size={14}/></button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
